import { useMemo, useState } from 'react';
import type { Deal, SaleListing } from '../api';
import { OfferTermsPanel } from './OfferTerms';


/** Which price a shopper is asking about: the multi-buy, or one case. */
type Basis = 'offer' | 'single';

/**
 * The two prices and the per-item figure, at the chosen basis.
 *
 * A bare "$5.33 $8.39" is ambiguous in the one way that matters — which is the
 * deal? — so the comparison price carries the word `reg`. And the per-item
 * figure names the pack size ("12 × $0.44") rather than saying "each", which on
 * a row already ending in a per-case price meant two different things.
 */
function pricing(listing: SaleListing, basis: Basis) {
  const offerPriced = !!listing.price.inOfferDisplay;
  const useOffer = basis === 'offer' && offerPriced;
  const main = useOffer ? listing.price.inOfferDisplay : listing.price.display;
  const compare = useOffer ? listing.price.display : listing.price.wasDisplay;
  const per = useOffer
    ? listing.unit?.perItemDisplay
    : listing.unit?.shelfPerItemDisplay ?? listing.unit?.perItemDisplay;
  const count = listing.unit?.count ?? null;
  const each = !per ? null : count && count > 1 ? `${count} × ${per}` : `${per} each`;
  return { main, compare, each };
}

function Prices({ listing, basis }: { listing: SaleListing; basis: Basis }) {
  const { main, compare, each } = pricing(listing, basis);
  return (
    <>
      <span className="listing-prices">
        {main && <strong>{main}</strong>}
        {compare && (
          <span className="listing-reg">
            reg <s>{compare}</s>
          </span>
        )}
      </span>
      {each && <em className="listing-each">{each}</em>}
    </>
  );
}

/**
 * The products a sale actually covers.
 *
 * "Soda 12-Packs at Target, 3 for $16" is one deal over twenty-one eligible
 * products, and the question is never what the headline says — it is whether
 * the thing you drink is in it. Three consequences, all of them visible here:
 *
 *   * past a screenful the strip becomes a searchable list, because that
 *     question is a lookup rather than a browse;
 *   * the per-item price is shown beside the pack price, since a shopper in an
 *     aisle is comparing cans, not boxes; and
 *   * the footnote says whether the retailer listed these or we read them off
 *     the page, because only one of those can honestly claim to be all of them.
 */
export function SaleContents({ deal }: { deal: Deal }) {
  const listings = deal.listings ?? [];
  const summary = deal.sale ?? null;
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  // Which price the list shows. The offer is the default because it is the deal
  // that was posted, but "what if I only want one" is a real question and the
  // shelf price is the honest answer to it.
  const [basis, setBasis] = useState<Basis>('offer');
  const offer = deal.price.offer;

  /** Past this many, browsing stops working and searching starts. */
  const STRIP_LIMIT = 6;
  const searchable = listings.length > STRIP_LIMIT;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return listings;
    return listings.filter((l) =>
      [l.title, l.brand, l.sku].filter(Boolean).join(' ').toLowerCase().includes(needle),
    );
  }, [listings, query]);

  if (!listings.length) return null;

  const visible = expanded || query ? matches : matches.slice(0, STRIP_LIMIT);

  return (
    <section className="sale-listings">
      <h3>
        <span>In this sale</span>
        <span className="sale-meta">
          {summary?.priceRangeDisplay && <em>{summary.priceRangeDisplay}</em>}
          {listings.length}
        </span>
      </h3>

      {offer && (
        <div className="sale-basis" role="group" aria-label="Price for how many">
          {(['offer', 'single'] as Basis[]).map((option) => (
            <button
              key={option}
              className="sale-basis-chip"
              aria-pressed={basis === option}
              onClick={() => setBasis(option)}
            >
              {option === 'offer' ? `Buy ${offer.quantity}` : 'Buy 1'}
            </button>
          ))}
        </div>
      )}

      {searchable && (
        <input
          className="sale-search"
          type="search"
          value={query}
          placeholder="Search this sale"
          aria-label="Search this sale"
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      {searchable ? (
        matches.length === 0 ? (
          <p className="sale-empty">Nothing in this sale matches “{query}”.</p>
        ) : (
          <>
            <ul className="listing-rows">
              {visible.map((listing, i) => (
                <ListingRow
                  key={listing.sku ?? listing.url ?? listing.title ?? i}
                  listing={listing}
                  basis={basis}
                />
              ))}
            </ul>
            {visible.length < matches.length && (
              <button className="sale-more" onClick={() => setExpanded(true)}>
                Show all {matches.length}
              </button>
            )}
          </>
        )
      ) : (
        <div className="listing-strip">
          {listings.map((listing, i) => (
            <ListingTile
              key={listing.sku ?? listing.url ?? listing.title ?? i}
              listing={listing}
              basis={basis}
            />
          ))}
        </div>
      )}

      {offer?.eachDisplay && (
        <p className="sale-offer">
          {basis === 'offer'
            ? `Buy ${offer.quantity} for ${offer.totalDisplay ?? ''} — ${offer.eachDisplay} a case`
            : `Prices for one. The ${offer.eachDisplay}-each deal needs ${offer.quantity}.`}
        </p>
      )}

      {offer && <OfferTermsPanel offer={offer} />}

      {summary && (
        <p className="sale-provenance">
          {summary.exact
            ? `All ${summary.count} products ${deal.merchant ?? 'the retailer'} lists for this offer.`
            : `${summary.count} products read from the deal page — the offer may cover more.`}
        </p>
      )}
    </section>
  );
}

function ListingTile({ listing, basis }: { listing: SaleListing; basis: Basis }) {
  return (
    <a
      className="listing-tile"
      href={listing.url ?? undefined}
      target="_blank"
      rel="noreferrer noopener"
      data-soldout={listing.inStock === false || undefined}
    >
      {listing.imageUrl ? (
        <img src={listing.imageUrl} alt="" loading="lazy" />
      ) : (
        <div className="listing-noimage" />
      )}
      <Prices listing={listing} basis={basis} />
      <span>{listing.title ?? 'Item'}</span>
    </a>
  );
}

function ListingRow({ listing, basis }: { listing: SaleListing; basis: Basis }) {
  return (
    <li className="listing-row" data-soldout={listing.inStock === false || undefined}>
      <a href={listing.url ?? undefined} target="_blank" rel="noreferrer noopener">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt="" loading="lazy" />
        ) : (
          <div className="listing-noimage small" />
        )}
        <div>
          <span className="listing-name">{listing.title ?? 'Item'}</span>
          <span className="listing-prices">
            <Prices listing={listing} basis={basis} />
            {listing.inStock === false && <span className="listing-gone">Sold out</span>}
          </span>
        </div>
      </a>
    </li>
  );
}
